import { getApiBase } from '@/lib/api-base';
import { fetchWebtokenHeadless } from '@/store/auth-helper';

/**
 * Clu's model router is the `router` variant of `clu_model_v1`, a multivariate
 * PostHog flag keyed by organization: the feature's one flag, with none of its
 * own. The backend reads the same flag and variant and enforces it. On any
 * other variant, with the flag off, or while flags load (no variant yet), the
 * router is off: every turn runs as it did before the router, a pick is
 * refused, and no answer names its model.
 */
export const CLU_MODEL_FLAG = 'clu_model_v1';
export const CLU_MODEL_ROUTER_VARIANT = 'router';

/**
 * The models a user can pick for a conversation: the router's ladder (GPT-6
 * Luna, its base, then GPT-6.1 Terra, GPT-6.1 Sol, Grok 4.7 and Sonnet 5.5)
 * and the GLMs, which Auto never moves to.
 */
export type CluModel =
  | 'luna'
  | 'terra'
  | 'sol'
  | 'grok'
  | 'flash'
  | 'glm'
  | 'sonnet55';
/** A conversation's pick: one model, or Auto (the router decides). */
export type CluModelPick = 'auto' | CluModel;
/** A model that answered a turn: a pickable one, or production's Sonnet 5. */
export type CluAnsweredModel = CluModel | 'sonnet5';

/** What the user is told when the server will not take a pick. */
export const CLU_MODEL_CHANGE_FAILED = 'Could not change the model.';

export const CLU_MODEL_PICKS: readonly {
  value: CluModelPick;
  label: string;
}[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'luna', label: 'GPT-6 Luna' },
  { value: 'terra', label: 'GPT-6.1 Terra' },
  { value: 'sol', label: 'GPT-6.1 Sol' },
  { value: 'grok', label: 'Grok 4.7' },
  { value: 'flash', label: 'GLM 5.3 Flash' },
  { value: 'glm', label: 'GLM 5.3' },
  { value: 'sonnet55', label: 'Sonnet 5.5' },
];

const ANSWERED_LABELS: Record<CluAnsweredModel, string> = {
  flash: 'GLM 5.3 Flash',
  glm: 'GLM 5.3',
  grok: 'Grok 4.7',
  sonnet55: 'Sonnet 5.5',
  luna: 'GPT-6 Luna',
  terra: 'GPT-6.1 Terra',
  sol: 'GPT-6.1 Sol',
  sonnet5: 'Sonnet 5',
};

// The router names a model by its OpenRouter id (the backend's
// copilot/model_router.py PICKS, from model_profiles.py): on the wire for a
// pick, in the stream's `model` event and in a conversation's model_router.
const MODEL_IDS: Record<CluAnsweredModel, string> = {
  flash: 'z-ai/glm-5.3-flash',
  glm: 'z-ai/glm-5.3',
  grok: 'x-ai/grok-4.7',
  sonnet55: 'anthropic/claude-sonnet-5.5',
  luna: 'openai/gpt-6-luna',
  terra: 'openai/gpt-6.1-terra',
  sol: 'openai/gpt-6.1-sol',
  sonnet5: 'claude-sonnet-5',
};
const MODELS_BY_ID = new Map(
  Object.entries(MODEL_IDS).map(([model, id]) => [
    id,
    model as CluAnsweredModel,
  ])
);

function isAnsweredModel(value: string): value is CluAnsweredModel {
  return Object.prototype.hasOwnProperty.call(ANSWERED_LABELS, value);
}

/** The model a stream event or a saved message names; null for none. */
export function cluAnsweredModelOf(value: unknown): CluAnsweredModel | null {
  if (typeof value !== 'string') return null;
  if (isAnsweredModel(value)) return value;
  return MODELS_BY_ID.get(value) ?? null;
}

/** A conversation's saved pick; Auto for none or anything unknown. */
export function cluModelPickOf(value: unknown): CluModelPick {
  const model = cluAnsweredModelOf(value);
  return model && model !== 'sonnet5' ? model : 'auto';
}

/** A pick as the router's API takes it: "auto" or the model's id. */
export function cluModelPickId(pick: CluModelPick): string {
  return pick === 'auto' ? 'auto' : MODEL_IDS[pick];
}

export function cluModelPickLabel(pick: CluModelPick): string {
  return (
    CLU_MODEL_PICKS.find((option) => option.value === pick)?.label ?? 'Auto'
  );
}

export function cluAnsweredModelLabel(model: CluAnsweredModel): string {
  return ANSWERED_LABELS[model];
}

/**
 * Saves a conversation's pick through the router's user-pick API
 * (`PUT /agent/conversations/{id}/model`): a model sticks for the
 * conversation and turns the router off there; Auto turns it back on. True
 * when the server kept it.
 */
export async function saveConversationModelPick(
  conversationId: string,
  pick: CluModelPick,
  current: () => boolean = () => true
): Promise<boolean> {
  try {
    if (!current()) return false;
    const token = await fetchWebtokenHeadless();
    if (!current()) return false;
    const res = await fetch(
      `${getApiBase()}/api/v1/agent/conversations/${conversationId}/model`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ model: cluModelPickId(pick) }),
      }
    );
    return current() && res.ok;
  } catch {
    return false;
  }
}
